import { API_BASE_URL, DONATION_ENDPOINTS } from '@/lib/stripe/config';
import { functions, auth } from '@/lib/firebase/config';
import { httpsCallable } from 'firebase/functions';

export type DonationPaymentMethod = 'card' | 'us_bank_account' | 'sepa_debit';

/**
 * The server already writes the sentence the donor should read — "this creator is
 * still setting up their payouts", a card decline, a platform outage. Three of
 * these calls used to throw a hard-coded "Failed to create..." and drop it on the
 * floor, so a donation to a creator whose payout account had not survived the
 * BackerBase move failed with a message that explained nothing.
 */
async function errorFrom(response: Response, fallback: string): Promise<Error> {
    try {
        const data = await response.json();
        if (data?.error) return new Error(String(data.error));
    } catch { /* no JSON body — use the fallback */ }
    return new Error(fallback);
}

export async function createOneOffDonation(params: {
    userId: string;
    creatorId: string;
    amount: number;
    currency: string;
    paymentMethod?: DonationPaymentMethod;
}) {
    // ✅ Get Firebase Auth token
    const user = auth.currentUser;
    if (!user) {
        throw new Error('You must be signed in to make a donation');
    }
    const token = await user.getIdToken();

    const response = await fetch(`${API_BASE_URL}${DONATION_ENDPOINTS.oneOff}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`, // ✅ Include auth token
        },
        body: JSON.stringify({
            userId: params.userId,
            creatorId: params.creatorId,
            amount: params.amount,
            currency: params.currency,
            paymentMethodTypes: params.paymentMethod ? [params.paymentMethod] : undefined,
        })
    });
    if (!response.ok) throw await errorFrom(response, 'Failed to create one-off donation');
    return response.json() as Promise<{ clientSecret: string; donationId: string }>;
}

export async function createMonthlySetup(params: {
    userId: string;
    creatorId: string;
    currency: string;
    paymentMethod: DonationPaymentMethod;
}) {
    // ✅ Get Firebase Auth token
    const user = auth.currentUser;
    if (!user) {
        throw new Error('You must be signed in to make a donation');
    }
    const token = await user.getIdToken();

    const response = await fetch(`${API_BASE_URL}${DONATION_ENDPOINTS.monthlySetup}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`, // ✅ Include auth token
        },
        body: JSON.stringify({
            userId: params.userId,
            creatorId: params.creatorId,
            currency: params.currency,
            paymentMethodTypes: [params.paymentMethod],
        })
    });
    if (!response.ok) throw await errorFrom(response, 'Failed to create setup intent');
    return response.json() as Promise<{ clientSecret: string; setupIntentId: string }>;
}

export async function confirmMonthlyDonation(params: {
    userId: string;
    creatorId: string;
    amount: number;
    currency: string;
    paymentMethodId: string;
    strategy: 'cancel_at' | 'iterations';
    endDateTs?: number;
    iterations?: number;
}) {
    // ✅ Get Firebase Auth token
    const user = auth.currentUser;
    if (!user) {
        throw new Error('You must be signed in to make a donation');
    }
    const token = await user.getIdToken();

    const response = await fetch(`${API_BASE_URL}${DONATION_ENDPOINTS.monthlyConfirm}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`, // ✅ Include auth token
        },
        body: JSON.stringify(params)
    });
    if (!response.ok) throw await errorFrom(response, 'Failed to confirm monthly donation');
    return response.json() as Promise<{ subscriptionId?: string; subscriptionScheduleId?: string; paymentIntentClientSecret?: string }>;
}

export async function createOneOffCheckout(params: {
    /** TSF-158: absent for a guest donation, which carries donorEmail instead. */
    userId?: string;
    donorEmail?: string;
    creatorId: string;
    amount: number;
    currency: string;
    paymentMethod: DonationPaymentMethod;
    successUrl?: string;
    cancelUrl?: string;
}) {
    // TSF-158: a donor no longer has to have an account. Signed in, we send the
    // ID token exactly as before; signed out, we send nothing and the server
    // accepts params.donorEmail instead. Never send a userId without a token --
    // the server rejects that combination.
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : null;

    const response = await fetch(`${API_BASE_URL}/createOneOffCheckout`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(user ? params : { ...params, userId: undefined })
    });
    if (!response.ok) throw await errorFrom(response, 'Failed to create checkout session');
    return response.json() as Promise<{ url: string; sessionId: string }>;
}

export async function createMonthlyCheckout(params: {
    /** TSF-158: absent for a guest donation, which carries donorEmail instead. */
    userId?: string;
    donorEmail?: string;
    creatorId: string;
    amount: number;
    currency: string;
    paymentMethod: DonationPaymentMethod;
    endStrategy?: 'cancel_at' | 'iterations';
    endDateTs?: number;
    iterations?: number;
    successUrl?: string;
    cancelUrl?: string;
}) {
    // TSF-158: a donor no longer has to have an account. Signed in, we send the
    // ID token exactly as before; signed out, we send nothing and the server
    // accepts params.donorEmail instead. Never send a userId without a token --
    // the server rejects that combination.
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : null;

    const response = await fetch(`${API_BASE_URL}/createMonthlyCheckout`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(user ? params : { ...params, userId: undefined })
    });
    if (!response.ok) throw await errorFrom(response, 'Failed to create checkout session');
    return response.json() as Promise<{ url: string; sessionId: string }>;
}

export async function createDonorPortal(params: { userId: string; creatorId?: string; donationId?: string; subscriptionId?: string; returnUrl?: string }) {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('You must be signed in to access the donor portal');
    }

    const token = await user.getIdToken();

    // First try callable to avoid any CORS/url routing issues
    try {
        const callable = httpsCallable(functions as any, 'createDonorPortalSessionCallable');
        const resp = await callable(params as any) as any;
        if (resp && resp.data && resp.data.url) {
            return { url: resp.data.url as string };
        }
    } catch (_) {
        // fall through to HTTP attempts
    }
    // Prefer direct Cloud Run URL if configured; fallback to Cloud Functions endpoints
    const portalFunctionBase = (import.meta as any).env?.VITE_PORTAL_FUNCTION_URL || 'https://createdonorportalsession-iozpk4dola-uc.a.run.app';
    const paths = [
        `${portalFunctionBase}`,
        `${portalFunctionBase}/createDonorPortalSession`,
        `${portalFunctionBase}/createdonorportalsession`,
        `${API_BASE_URL}/createDonorPortalSession`,
        `${API_BASE_URL}/createdonorportalsession`
    ];
    let lastErr: any = null;
    for (const p of paths) {
        try {
            const res = await fetch(p, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`, // ✅ Include auth token
                },
                body: JSON.stringify(params)
            });
            if (!res.ok) {
                lastErr = new Error(await res.text());
                continue;
            }
            return res.json() as Promise<{ url: string }>;
        } catch (e: any) {
            lastErr = e;
        }
    }
    throw new Error('Failed to create portal session');
}

export async function fetchInvoiceLink(params: { paymentId?: string; donationId?: string; stripePaymentIntentId?: string; stripeInvoiceId?: string; stripeSubscriptionId?: string; createdAtMs?: number }) {
    // First try callable (no CORS/deploy friction)
    try {
        const callable = httpsCallable(functions as any, 'getInvoiceLinkCallable');
        const resp = await callable(params as any) as any;
        if (resp?.data?.url) return { url: resp.data.url as string };
    } catch (e) {
        console.warn('[fetchInvoiceLink] callable failed', e);
    }
    // Fallback to HTTP if callable not available
    // Use the direct Cloud Run URL for gen2 functions
    const endpoints = [
        'https://getinvoicelink-iozpk4dola-uc.a.run.app',
        `${API_BASE_URL}/getInvoiceLink`,
    ];
    // The endpoint now requires the caller to own the donation (a receipt shows
    // the donor's name, email, billing address and amount), so the fallback has
    // to carry the token the callable sends automatically.
    const user = auth.currentUser;
    if (!user) throw new Error('Sign in to view a receipt');
    const token = await user.getIdToken();

    let lastError: any = null;
    for (const url of endpoints) {
        try {
            console.log('[fetchInvoiceLink] trying', url);
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(params)
            });
            if (!response.ok) {
                lastError = new Error(await response.text());
                console.warn('[fetchInvoiceLink] failed', url, response.status);
                continue;
            }
            console.log('[fetchInvoiceLink] success', url);
            return response.json() as Promise<{ url: string }>;
        } catch (e: any) {
            lastError = e;
            console.warn('[fetchInvoiceLink] error', url, e);
        }
    }
    throw lastError || new Error('Invoice not ready');
}


